import { useEffect, useState } from 'react';
import { isFeatureOn } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { patchCachedOrder } from '../lib/cached';
import type { DriverRow, OrderRow } from '../lib/types';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

interface BatchResult {
  results: Array<{
    orderId: string;
    status: 'SUCCESS' | 'FAILED';
    newStatus?: string;
    error?: { code: string; message: string };
  }>;
  summary: { total: number; succeeded: number; failed: number };
}

/**
 * 派单对话框。
 *
 * ★ 两条关键设计(docs/03-order-lifecycle.md §3.5):
 *   1. **负载可见** —— 看不到司机手上有多少单,调度员只能凭感觉,
 *      必然导致某个司机被压垮
 *   2. **部分成功是常态** —— 返回逐单结果,失败的说明原因,
 *      而不是甩一个「成功 34 失败 3」
 */
export function AssignDialog({
  features,
  orders,
  drivers,
  onClose,
  onDone,
}: {
  orders: OrderRow[];
  drivers: DriverRow[];
  onClose: () => void;
  onDone: () => Promise<void>;
  /** 租户功能开关 —— 决定派完之后的提示语 */
  features?: Record<string, boolean>;
}) {
  /*
   * ★ 派单有两条完全不同的路:派给自己的司机,或推给第三方平台。
   *   放在同一个对话框里用分段切换,而不是两个入口 —— 调度员面对的是
   *   同一个决定(「这批单交给谁送」),只是答案有两类。
   */
  const [mode, setMode] = useState<'DRIVER' | 'CHANNEL'>('DRIVER');
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [channelCode, setChannelCode] = useState<string | null>(null);
  const [driverId, setDriverId] = useState<string | null>(null);
  const assignWithoutAccept = isFeatureOn(features, 'assignWithoutAccept');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const available = drivers.filter((d) => d.status === 'ACTIVE');

  useEffect(() => {
    // 读不到通道(没权限)就当没有这条路,不弹错 ——
    // 大部分调度员根本没有 integration:read
    void api<ChannelOption[]>('/integrations/channels')
      .then((all) => setChannels(all.filter((c) => c.isActive)))
      .catch(() => setChannels([]));
  }, []);

  /**
   * 推给第三方。
   *
   * ★ 只排队,不等发完:一次两百单是两百次外部 HTTP,
   *   挂在这个请求上的话浏览器早就超时了,而任务已经发出去一半 ——
   *   操作的人根本不知道发到第几单。
   */
  async function pushToChannel() {
    if (!channelCode || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<DispatchResult>('/integrations/dispatch', {
        method: 'POST',
        body: {
          orderIds: orders.map((o) => o.id),
          channelCode,
          operation: 'CREATE_ORDER',
        },
      });
      setResult({
        // 「已经排过队」也算成功,但要和「刚排进去」区分开 ——
        // 否则会以为又推了一次
        results: r.results.map((x) => ({
          orderId: x.orderId,
          status: x.status === 'FAILED' ? ('FAILED' as const) : ('SUCCESS' as const),
          newStatus: x.status === 'DUPLICATED' ? '之前已排队' : '已排队',
          ...(x.error ? { error: { code: '', message: x.error } } : {}),
        })),
        summary: {
          total: r.summary.total,
          succeeded: r.summary.queued + r.summary.duplicated,
          failed: r.summary.failed,
        },
      });
      if (r.summary.failed === 0) await onDone();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '推送失败');
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!driverId || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<BatchResult>('/orders/batch-assign', {
        method: 'POST',
        body: {
          driverId,
          orderIds: orders.map((o) => o.id),
          batchKey: `assign-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`,
        },
      });
      setResult(r);
      /*
       * ★ 成功的那几单,就地改掉本地缓存里的状态。
       *   响应里带着每单的 newStatus —— 不用猜(派单免确认开着的话
       *   落的是 ACCEPTED 而不是 ASSIGNED,猜错写进缓存比不写更糟)。
       */
      for (const one of r.results) {
        if (one.status !== 'SUCCESS' || !one.newStatus) continue;
        void patchCachedOrder(one.orderId, { status: one.newStatus });
      }
      // 全部成功就直接关闭;有失败则停留让调度员看清楚
      if (r.summary.failed === 0) await onDone();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '派单失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-dialog-title"
        className="w-full max-w-2xl max-h-[85dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b
                        border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 id="assign-dialog-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            派发订单(已选 {orders.length} 单)
          </h2>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl"><InlineIcon icon={X} /></button>
        </div>

        <div className="p-4 lg:p-6">
          {result ? (
            <ResultView result={result} orders={orders} onDone={onDone} />
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                                dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {/* 没配通道就不显示切换 —— 一个永远只有一个选项的分段控件只是噪音 */}
              {channels.length > 0 && (
                <div className="mb-4 flex gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-900/50">
                  {(
                    [
                      ['DRIVER', '派给司机'],
                      ['CHANNEL', '推给第三方平台'],
                    ] as const
                  ).map(([m, label]) => (
                    <button
                      key={m}
                      onClick={() => setMode(m)}
                      className={`flex-1 h-9 rounded-lg text-sm font-medium transition ${
                        mode === m
                          ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                          : 'text-slate-500'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {mode === 'CHANNEL' ? (
                <ChannelPicker
                  channels={channels}
                  value={channelCode}
                  onChange={setChannelCode}
                />
              ) : (
              <>
              <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                选择司机
              </h3>

              {available.length === 0 ? (
                <p className="text-slate-400 py-6 text-center">没有可用司机</p>
              ) : (
                <div className="space-y-2 mb-6">
                  {available.map((d) => {
                    // 这个数和并发守卫判断用的是同一个口径(见 on-plate.ts)
                    const load = d.currentOrderCount;
                    // 负载条:让调度员一眼看出谁快满了
                    const pct = Math.min(100, (load / 30) * 100);
                    const heavy = load >= 20;
                    const offline = d.dutyStatus === 'OFFLINE';

                    return (
                      <label
                        key={d.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition
                          ${driverId === d.id
                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}
                      >
                        <input
                          type="radio"
                          name="driver"
                          checked={driverId === d.id}
                          onChange={() => setDriverId(d.id)}
                          className="w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 dark:text-slate-100">
                              {d.name}
                            </span>
                            <span className="text-xs text-slate-400">{d.driverNo}</span>
                            {offline && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100
                                               dark:bg-slate-700 text-slate-500">
                                离线
                              </span>
                            )}
                            {heavy && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100
                                               dark:bg-amber-950 text-amber-700 dark:text-amber-400">
                                负载高
                              </span>
                            )}
                            {/*
                              ★ 「这个人接不接单」要出现在**挑司机的这一刻**。
                                只放在司机列表上的话,调度不会为了派一单
                                先去另一个页面查一遍 —— 他会直接派,
                                然后过十五分钟发现单子被系统收回来了。
                            */}
                            {(d.timeout7d ?? 0) >= 3 && (
                              <span
                                className="text-xs px-1.5 py-0.5 rounded bg-red-100
                                           dark:bg-red-950 text-red-700 dark:text-red-400"
                                title="近 7 天派单后超时未接的次数"
                              >
                                7 天内超时 {d.timeout7d} 次
                              </span>
                            )}
                          </div>
                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${heavy ? 'bg-amber-500' : 'bg-green-500'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-500 tabular-nums w-16 text-right">
                              手上 {load} 单
                            </span>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              </>
              )}

              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 h-11 rounded-lg border border-slate-300 dark:border-slate-600
                             text-slate-700 dark:text-slate-300"
                >
                  取消
                </button>
                {mode === 'CHANNEL' ? (
                  <button
                    onClick={() => void pushToChannel()}
                    disabled={!channelCode || busy}
                    className="flex-1 h-11 rounded-lg bg-brand-600 text-white font-medium
                               disabled:opacity-40"
                  >
                    {busy ? '推送中…' : `推送 ${orders.length} 单`}
                  </button>
                ) : (
                  <button
                    onClick={() => void submit()}
                    disabled={!driverId || busy}
                    className="flex-1 h-11 rounded-lg bg-brand-600 text-white font-medium
                               disabled:opacity-40"
                  >
                    {busy ? '派发中…' : `确认派发 ${orders.length} 单`}
                  </button>
                )}
              </div>
              {/*
                ★ 说清楚派完之后会发生什么 —— 两种情形差别很大:
                  开了「派单免确认」是「派完这单就是他的了」,
                  没开是「他还要点一次接单,超时不点会回到待派」。
                  调度据此决定要不要盯着、要不要打电话催。
              */}
              {mode === 'DRIVER' && (
                <p className="mt-2 text-xs text-slate-500">
                  {assignWithoutAccept
                    ? '派完直接进入「待取货」,司机不用再确认'
                    : '派完之后要司机在手机上点一次「接单」;超时没点会自动回到待派'}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface ChannelOption {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  environment: string;
  operations: string[];
}

interface DispatchResult {
  results: Array<{ orderId: string; status: string; jobId?: string; error?: string }>;
  summary: { total: number; queued: number; duplicated: number; failed: number };
}

/** 选一个三方通道 */
function ChannelPicker({
  channels,
  value,
  onChange,
}: {
  channels: ChannelOption[];
  value: string | null;
  onChange: (code: string) => void;
}) {
  return (
    <div className="mb-6 space-y-2">
      {channels.map((c) => {
        // 没配建单映射的通道推过去必然失败 —— 在这里就说清楚
        const ready = c.operations.includes('CREATE_ORDER');
        return (
          <label
            key={c.id}
            className={`flex items-center gap-3 p-3 rounded-xl border transition
              ${ready ? 'cursor-pointer' : 'opacity-60 cursor-not-allowed'}
              ${
                value === c.code
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
          >
            <input
              type="radio"
              name="channel"
              disabled={!ready}
              checked={value === c.code}
              onChange={() => onChange(c.code)}
              className="w-4 h-4"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 dark:text-slate-100">{c.name}</span>
                {c.environment === 'SANDBOX' && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950
                                   text-amber-700 dark:text-amber-400">
                    沙箱 —— 对方不会真的派车
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                {ready ? '推过去之后由对方派送,状态靠回调同步' : '这个通道还没配建单映射,推不了'}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

/** 逐单结果。失败的必须说清原因,不能只给个数字 */
function ResultView({
  result,
  orders,
  onDone,
}: {
  result: BatchResult;
  orders: OrderRow[];
  onDone: () => Promise<void>;
}) {
  const failed = result.results.filter((r) => r.status === 'FAILED');
  const orderNo = (id: string) => orders.find((o) => o.id === id)?.orderNo ?? id;

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <div className="text-green-600 dark:text-green-400">
          ✓ 成功 <span className="font-semibold tabular-nums">{result.summary.succeeded}</span>
        </div>
        {result.summary.failed > 0 && (
          <div className="text-red-600 dark:text-red-400">
            <InlineIcon icon={X} /> 失败 <span className="font-semibold tabular-nums">{result.summary.failed}</span>
          </div>
        )}
      </div>

      {failed.length > 0 && (
        <div className="rounded-xl border border-red-200 dark:border-red-900 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300">
              <tr>
                <th className="p-2.5 text-left font-medium">单号</th>
                <th className="p-2.5 text-left font-medium">原因</th>
              </tr>
            </thead>
            <tbody>
              {failed.map((f) => (
                <tr key={f.orderId} className="border-t border-red-100 dark:border-red-900/50">
                  <td className="p-2.5 font-mono text-xs">{orderNo(f.orderId)}</td>
                  <td className="p-2.5 text-slate-700 dark:text-slate-300">
                    {f.error?.message ?? '未知错误'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button
        onClick={() => void onDone()}
        className="w-full h-11 rounded-lg bg-brand-600 text-white font-medium"
      >
        知道了
      </button>
    </div>
  );
}
